import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useResetPassword } from '../../../hooks/profile/useResetPassword'

import Icon from '../../ui/Icon'
import { Button } from '../../buttons/Button'
import { PasswordCreateInput } from '../../input/passwordCreateInput/PasswordCreateInput'

import { useApp } from '../../../contexts/AppContext'
import { FormField } from '../../../types/formData'
import { BannerKind } from '../../../types/contexts/AppContext'

interface ResetPasswordFormData {
  password: FormField<string>
}

export const ResetPasswordForm = () => {
  const { t } = useTranslation(['forgot'])
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const [searchParams] = useSearchParams()
  const tokenId = searchParams.get('rtid')
  const resetPassword = useResetPassword()
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    password: { value: '', isValid: false },
  })

  useEffect(() => {
    if (!tokenId) {
      navigate('/forgot-password', { replace: true })
    }
  }, [navigate, tokenId])

  useEffect(() => {
    if (resetPassword.success) {
      resetPassword.resetState()
      navigate('/login')
    }
    if (resetPassword.error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: resetPassword.error,
      })
      resetPassword.resetState()
    }
  }, [resetPassword, navigate, showBanner])

  const handleInputChange = (_name: string, value: string, isValid: boolean) => {
    setFormData({ password: { value, isValid } })
  }

  const validForm = () => Object.values(formData).every(input => input.isValid)

  const handleResetPassword = async () => {
    if (!tokenId) {
      return
    }

    await resetPassword.resetPassword({
      tokenId,
      newPassword: formData.password.value,
    })
  }

  if (!tokenId) return null
  return (
    <form className="fly-form">
      <Button
        className="fly-btn-back"
        variant="ghost"
        size="sm"
        icon={<Icon name="chevL" size={14} />}
        label={t('forgot:back')}
        onClick={() => navigate('/')}
      />

      <div className="eyebrow">{t('forgot:eyebrow')}</div>
      <h1 className="display fly-text-title">{t('forgot:reset-title')}</h1>
      <p className="fly-text-subtitle">{t('forgot:reset-subtitle')}</p>
      <div className="ph-col">
        <PasswordCreateInput
          label={t('forgot:reset-password-label')}
          placeHolder={t('forgot:reset-password-placeholder')}
          confirmLabel={t('forgot:reset-confirm-label')}
          confirmPlaceHolder={t('forgot:reset-confirm-placeholder')}
          name="password"
          value={formData.password.value}
          onChange={handleInputChange}
          errorMessage={t('register:password-error')}
        />

        <Button
          className="btn-submit"
          size="lg"
          block
          loading={resetPassword.loading}
          disabled={!validForm()}
          label={
            resetPassword.loading
              ? t('forgot:reset-loading')
              : t('forgot:reset-submit')
          }
          icon={<Icon name="arrowR" size={16} />}
          iconPosition="right"
          onClick={handleResetPassword}
        />
      </div>
    </form>
  )
}
